import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  chakra,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  VStack,
  useTheme,
} from "@chakra-ui/react";
import { CheckCircle, Clock, DotsThreeVertical, Trash, XCircle } from "@phosphor-icons/react";

// A session nobody has touched yet: still sitting at the initial workflow status with no file attached.
// `status` alone is not enough — a session with a file waiting on the agent's next tool call is also
// "created" — so both conditions have to hold. Used to keep these off the picker (there's nothing to
// resume) and to decide whether opening AI Mapping can skip the picker entirely and reuse one instead of
// piling up another empty session.
export function isEmptySession(s) {
  return s?.status === "created" && !s?.source_file_name;
}

// `now` is passed in rather than read via `Date.now()` inside — otherwise this only recomputes when
// something else happens to re-render the picker, so "just now" can sit there unchanged for as long as the
// modal is open. The interval below in `SessionPicker` is what actually keeps it moving.
function formatRelative(timestamp, now) {
  const seconds = Math.max(0, now / 1000 - timestamp);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function SessionPicker({
  sessions,
  totalCount,
  maxSessions,
  onContinue,
  onDelete,
  onNew,
  isCreating,
  createError,
  showInfoBanner = true,
  // Per-session "did this session actually write into the task" summary, keyed by session id. Null means
  // the caller does not track commits — a Foundation Data v2 session saves into a curated set and has no
  // task to have written into — and the row is then left off entirely rather than reporting "No data added
  // to this task" on evidence nobody collected.
  commitsBySessionId = null,
  // What these sessions are scoped to, in prose. Defaults to the carbon wording so the AI Mapping page is
  // unchanged; a Foundation Data v2 session is scoped to a year and has no destination to name, and telling
  // that user their sessions belong to "this data task" describes something they never chose.
  scopeLabel = "this data task and destination",
}) {
  const [deletingId, setDeletingId] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const { colors } = useTheme();

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  // The server sends 0 when session quotas are off, which is now the default. `length >= 0` is always
  // true, so treating 0 as a real ceiling would permanently disable the button that creates sessions —
  // the exact opposite of removing the cap.
  //
  // Checked against `totalCount` (every session the server counts toward the quota), not `sessions.length`
  // (only the ones worth showing) — empty sessions are hidden from the list below but still occupy a slot,
  // so the button has to stay disabled at the real ceiling even when the visible list looks short.
  const hasLimit = Number(maxSessions) > 0;
  const atLimit = hasLimit && (totalCount ?? sessions.length) >= maxSessions;

  return (
    <>
      {showInfoBanner && (
        <Alert status="info" variant="left-accent" mb={5} borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Sessions are saved, so you can close this page and pick up where you left off. These are the
            sessions for {scopeLabel}.
            {hasLimit
              ? ` Your organisation can have up to ${maxSessions} active sessions at a time — delete one to free a slot.`
              : ""}
          </AlertDescription>
        </Alert>
      )}

      {createError && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">{createError}</AlertDescription>
        </Alert>
      )}

      <Box
        w="100%"
        border="1px solid"
        borderColor="lime"
        borderRadius="lg"
        bg="lightLime"
        _hover={{ borderColor: "limeShade.400" }}
        transition="all 0.15s"
        overflow="hidden"
        position="relative"
        px={6}
        pt={5}
        pb={8}
        mb={5}
      >
        <chakra.svg
          position="absolute"
          right="0"
          bottom="0"
          width="220px"
          height="180px"
          viewBox="0 0 175 114"
          preserveAspectRatio="xMaxYMax meet"
          fill="none"
          pointerEvents="none"
        >
          <path
            d="M31.2441 113.272C31.2066 113.31 31.1694 113.387H0C4.3128 108.084 9.24419 102.6 14.8643 96.9404L86.3467 25.458L64.8271 25.4736L64.7959 2.35255H142.156L31.2441 113.272Z"
            fill="#85C22A"
            fillOpacity="0.15"
          />
          <path
            d="M174.876 18.7002L80.2031 113.387H47.501L158.521 2.36036H174.876V18.7002Z"
            fill="#85C22A"
            fillOpacity="0.15"
          />
          <path
            d="M174.884 112.425H174.999V113.387H174.87C174.873 113.069 174.876 112.751 174.876 112.433L151.763 112.362V90.6221L128.998 113.387H96.2949L174.884 34.7969V112.425Z"
            fill="#85C22A"
            fillOpacity="0.15"
          />
        </chakra.svg>
        <Text fontSize="22px" fontWeight="700" color="darkTeal" mb={3}>
          New Session
        </Text>
        <Text fontSize="14px" color="gray.600" mb={5} maxW="70%" position="relative" zIndex={1}>
          Upload or import a new source file to map into this task.
        </Text>
        <Flex gap={2} position="relative" zIndex={1}>
          <Button
            bg="limeShade.600"
            color="white"
            borderRadius="full"
            _hover={{ bg: "limeShade.700" }}
            size="sm"
            onClick={onNew}
            isLoading={isCreating}
            isDisabled={atLimit}
            title={atLimit ? `Limit of ${maxSessions} sessions reached` : undefined}
          >
            Start a new session
          </Button>
        </Flex>
        {atLimit && (
          <Text mt={3} fontSize="sm" color="gray.600" position="relative" zIndex={1}>
            You&apos;ve reached the limit of {maxSessions} sessions. Delete one to
            start a new session.
          </Text>
        )}
      </Box>

      <VStack align="stretch" spacing={3} mb={5}>
        {sessions.map((s) => (
          <Box
            key={s.id}
            position="relative"
            p={4}
            bg="gray.50"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            boxShadow="sm"
            cursor={deletingId === s.id ? "default" : "pointer"}
            transition="all 0.15s"
            _hover={{ borderColor: "lime" }}
            onClick={() => {
              if (deletingId !== s.id) onContinue(s);
            }}
          >
            {/* Stops the click from reaching the card's onClick above — otherwise opening the menu (or
                choosing Delete) also navigates into the session it's trying to remove. */}
            <Box position="absolute" top={2} right={2} onClick={(e) => e.stopPropagation()}>
              <Menu isLazy placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  aria-label="Session actions"
                  icon={<DotsThreeVertical size={20} weight="bold" />}
                  variant="ghost"
                  color="gray.500"
                  _hover={{ color: "gray.700" }}
                  size="sm"
                  borderRadius="full"
                  isLoading={deletingId === s.id}
                />
                <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1} minW="160px">
                  <MenuItem
                    icon={<Trash size={16} color="#E53E3E" />}
                    onClick={() => handleDelete(s.id)}
                    fontSize="sm"
                    color="red.500"
                  >
                    Delete
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>

            <Text fontSize="15px" fontWeight="600" color="gray.800" mb={2} pr={8} isTruncated>
              {s.source_file_name || "Untitled session"}
            </Text>

            <VStack align="stretch" spacing={1} mb={3}>
              <HStack spacing={2}>
                <Clock size={12} color={colors.gray[500]} />
                <Text fontSize="12px" color="gray.500">
                  Last opened {formatRelative(s.last_accessed_at, now)}
                </Text>
              </HStack>
              {/* The session's own status (Analyzing/Mapping/...) is just the agent's workflow stage — it
                  never reflects whether this session actually wrote anything into the task, since
                  committing writes to the task directly rather than back into the session. This is the
                  only signal on the card for that, either way. */}
              {commitsBySessionId &&
                (commitsBySessionId[s.id] ? (
                  <HStack spacing={2}>
                    <CheckCircle size={12} color={colors.green[600]} weight="fill" />
                    <Text fontSize="12px" color="green.700">
                      {commitsBySessionId[s.id].rowsWritten}{" "}
                      {commitsBySessionId[s.id].rowsWritten === 1 ? "row" : "rows"} added to this task
                    </Text>
                  </HStack>
                ) : (
                  <HStack spacing={2}>
                    <XCircle size={12} color={colors.orange[500]} weight="fill" />
                    <Text fontSize="12px" color="orange.600">
                      No data added to this task
                    </Text>
                  </HStack>
                ))}
            </VStack>
          </Box>
        ))}
      </VStack>
    </>
  );
}

export function SessionPickerLoading() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" h="80vh">
      <Spinner size="md" mr={3} />
      <Text color="gray.600">Loading sessions...</Text>
    </Box>
  );
}

export default SessionPicker;
